'use strict';

const { payloadKeySeparator } = require('./notifications');

// baseTranslation in translations json file should contain starting/finished/failed keys
// actions can include starting/finished/failed keys - the action names should include the module
// payloadKey is the payload used in the notification
//   when using composite keys with payloadKeySeparator, the first value is the main payload used in the notification.
//   the second payload value may be used for logic. e.g. run_pricing_for_all uses run_kpis_for_all translation when skip_optimisation=true.
// Analytics notifications payload is sent in status_updaters.py HandlerFactory class
// https://owlabs.atlassian.net/wiki/spaces/RTLS/pages/4347494520/Analytics+notifications+after+JobApi+removal
module.exports = {
  functions: {
    run_pricing: {
      baseTranslation: 'notifications.run_pricing',
      actions: {
        finished: 'gridView/updateScenarioResults',
        failed: 'gridView/updateScenarioResults',
      },
      payloadKey: 'scenario_key',
    },
    run_pricing_for_all: {
      baseTranslation: 'notifications.run_pricing_for_all',
      skipOptimisationTranslation: 'notifications.run_kpis_for_all',
      payloadKey: `workpackage_id${payloadKeySeparator}skip_optimisation`,
      actions: {
        finished: 'gridView/postRunEngineForAllScenarios',
        failed: 'gridView/postRunEngineForAllScenarios',
        // handle case where engine is started by argo. e.g. transfer workpackage.
        // doesn't start from runEngineForAllScenarios so setRecalculatingAll needs to be called from checkRecalculatingAll
        starting: 'gridView/checkRecalculatingAll',
      },
    },
    // TODO: rename, run_pricing_for_pgs runs for a single level_entry_key.
    // run_pricing_for_specific_pgs runs multiple level_entry_keys.
    run_pricing_for_pgs: {
      baseTranslation: 'notifications.run_pricing_for_pgs',
      payloadKey: `workpackage_id${payloadKeySeparator}level_entry_key`,
      actions: {
        finished: 'gridView/postRunEngineForPricingGroups',
        failed: 'gridView/postRunEngineForPricingGroups',
      },
    },
    run_pricing_for_specific_pgs: {
      baseTranslation: 'notifications.run_pricing_for_specific_pgs',
      payloadKey: `workpackage_id${payloadKeySeparator}level_entry_keys`,
      actions: {
        finished: 'gridView/postRunEngineForSpecificPricingGroups',
        failed: 'gridView/postRunEngineForSpecificPricingGroups',
      },
    },
    run_refresh_workpackage: {
      baseTranslation: 'notifications.run_refresh_workpackage',
      payloadKey: 'workpackage_id',
      actions: {
        finished: 'workpackages/afterRefreshWorkpackage',
        failed: 'workpackages/afterFailedRefreshWorkpackage',
      },
    },
    run_create_workpackage: {
      baseTranslation: 'notifications.run_create_workpackage',
      payloadKey: 'description', // name of the new WP
      actions: {
        finished: 'workpackages/afterCreateWorkpackage',
        failed: 'workpackages/afterFailedCreateWorkpackage',
      },
    },
    run_create_workpackage_dag: {
      baseTranslation: 'notifications.run_create_workpackage',
      payloadKey: 'description', // name of the new WP
      actions: {
        finished: 'workpackages/afterCreateWorkpackageDag',
        failed: 'workpackages/afterFailedCreateWorkpackage',
      },
    },
    create_scenarios: {
      baseTranslation: 'notifications.create_scenarios',
      actions: {
        finished: 'scenarioMetadata/afterCreateScenario',
        failed: 'scenarioMetadata/afterCreateScenario',
      },
      payloadKey: 'scenario_description',
    },
    run_delete_workpackage: {
      baseTranslation: 'notifications.run_delete_workpackage',
      payloadKey: 'workpackage_id',
      actions: {
        finished: 'workpackages/afterDeleteWorkpackage',
        failed: 'workpackages/afterDeleteWorkpackage',
      },
    },
    run_clone_workpackage: {
      baseTranslation: 'notifications.run_clone_workpackage',
      payloadKey: 'new_workpackage_description',
      actions: {
        finished: 'workpackages/afterCloneWorkpackage',
        failed: 'workpackages/afterCloneWorkpackage',
      },
    },
    delete_scenario: {
      baseTranslation: 'notifications.delete_scenario',
      actions: {
        finished: 'scenarioMetadata/afterDeleteScenario',
        failed: 'scenarioMetadata/afterDeleteScenario',
      },
      payloadKey: 'description', // name of the deleted scenario
    },
    update_hierarchy: {
      baseTranslation: 'notifications.update_hierarchy',
      actions: {
        finished: 'attributes/postHierarchyUpload',
      },
    },
    // TODO: handle other failure states.
    // if finished hook sets loading to false, failure should also set to false to avoid infinite spinners.
    update_attributes: {
      baseTranslation: 'notifications.update_attributes',
      actions: {
        finished: 'attributes/postAttributeUpload',
      },
    },
    update_competitors: {
      baseTranslation: 'notifications.update_competitors',
      actions: {
        finished: 'attributes/postCompetitorUpload',
      },
    },
    update_engine_inputs: {
      baseTranslation: 'notifications.update_engine_inputs',
      actions: {
        finished: 'attributes/postEngineInputsUpload',
      },
    },
    export_penalty_data: {
      baseTranslation: 'notifications.export_penalty_data',
    },
    transfer_workpackage: {
      baseTranslation: 'notifications.transfer_workpackage',
      actions: {
        finished: 'workpackages/afterTransferWorkpackage',
        failed: 'workpackages/afterTransferWorkpackage',
      },
      payloadKey: 'destination_workpackage_id', // id of the WP
    },
    export_workpackage: {
      baseTranslation: 'notifications.export_workpackage',
      payloadKey: 'description', // name of the WP
    },
    run_wholesale_engine: {
      baseTranslation: 'notifications.run_wholesale_engine',
      payloadKey: 'workpackage_id', // id of the WP
      actions: {
        finished: 'wholesale/reloadWholesaleDataAfterEngineRun',
        failed: 'wholesale/reloadWholesaleDataAfterEngineRun',
      },
    },
    run_wholesale_export: {
      baseTranslation: 'notifications.run_wholesale_export',
      payloadKey: 'workpackage_id', // id of the WP
      actions: {
        finished: 'wholesale/fetchWholesaleExports',
        failed: 'wholesale/handleWholesaleExportFailed',
        running: 'wholesale/fetchWholesaleExports',
      },
    },
    create_wholesale_grid: {
      baseTranslation: 'notifications.create_wholesale_grid',
      payloadKey: 'gridDescription',
    },
    upload_wholesale_grid: {
      baseTranslation: 'notifications.upload_wholesale_grid',
    },
    export_wholesale_results: {
      baseTranslation: 'notifications.export_wholesale_results',
      payloadKey: 'description', // name of the WP
    },
    export_line_pricing: {
      baseTranslation: 'notifications.export_line_pricing',
      payloadKey: 'description', // name of the WP
    },
    update_line_pricing_groups: {
      baseTranslation: 'notifications.update_line_pricing_groups',
      payloadKey: 'workpackage_id',
      events: {
        finished: 'postLinePricingGroupsUpload',
        failed: 'postLinePricingGroupsUpload',
      },
    },
    recalculate_kpis: {
      baseTranslation: 'notifications.recalculate_kpis',
      payloadKey: 'product_key',
    },
    save_kpi_results_to_databricks: {
      baseTranslation: 'notifications.save_kpi_results_to_databricks',
      payloadKey: 'workpackage_id',
    },
    competitor_price_simulation: {
      baseTranslation: 'notifications.competitor_price_simulation',
      payloadKey: 'workpackage_id',
    },
    run_refresh_similarity_groups: {
      baseTranslation: 'notifications.run_refresh_similarity_groups',
      payloadKey: 'workpackage_id',
      events: {
        finished: 'refreshSimilarityGroupsFinished',
        failed: 'refreshSimilarityGroupsFinished',
      },
    },
    update_similarity_groups: {
      baseTranslation: 'notifications.update_similarity_groups',
      events: {
        finished: 'updateSimilarityGroupsImportFinished',
      },
    },
    refresh_cross_elasticity_parameters: {
      baseTranslation: 'notifications.refresh_cross_elasticity_parameters',
      payloadKey: 'workpackage_id',
      events: {
        finished: 'refreshXeoParamsFinished',
        failed: 'refreshXeoParamsFinished',
      },
    },
    run_cross_elasticity_optimisation: {
      baseTranslation: 'notifications.run_cross_elasticity_optimisation',
      payloadKey: 'scenario_key',
      actions: {
        finished: 'gridView/updateScenarioResults',
        failed: 'gridView/updateScenarioResults',
      },
    },
    run_margin_initialisation: {
      baseTranslation: 'notifications.run_margin_initialisation',
      payloadKey: 'workpackage_id',
      events: {
        finished: 'runMarginInitialisationFinished',
        failed: 'runMarginInitialisationFinished',
      },
    },
    run_refresh_forecast_volume: {
      baseTranslation: 'notifications.run_refresh_forecast_volume',
      payloadKey: 'workpackage_id',
      events: {
        finished: 'runRefreshForecastVolumeFinished',
        failed: 'runRefreshForecastVolumeFinished',
      },
    },
    override_prices: {
      baseTranslation: 'notifications.override_prices',
      payloadKey: 'scenario_key',
    },
    run_refresh_alerts: {
      baseTranslation: 'notifications.run_refresh_alerts',
      payloadKey: 'workpackage_id',
      actions: {
        starting: 'gridView/startRefreshingAlerts',
        finished: 'gridView/postRunRefreshAlerts',
        failed: 'gridView/postRunRefreshAlerts',
      },
    },
  },
};
